import { useState } from "react";
import { Spin, App } from "antd";
import { useOutletContext, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MenuOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { useDevices, useCreateDevice } from "../../../../hooks/useDevices";
import { useUsers } from "../../../../hooks/useUsers";
import { useOrganization } from "../../../../hooks/useOrganization";
import ProfileDropdown from "../../../../components/ProfileDropdown";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { logout, setAccess } from "../../../../store/authSlice";
import { denyAccess } from "../../../../api/access";
import type { ConfigOutletContext } from "../../Configuration";
import DeviceFormModal, { type DeviceFormValues } from "./DeviceFormModal";
import DevicesTable from "./DevicesTable";
import EdgeStatusPanel from "./EdgeStatusPanel";

const R = {
  bg: "var(--color-canvas)",
  surface: "var(--color-surface)",
  border: "var(--color-shade-2)",
  textSoft: "var(--color-body)",
  ink: "var(--color-ink)",
  brandTint: "var(--color-brand-tint)",
  danger: "var(--color-danger-ink)",
};

const DevicesPage = () => {
  const { t } = useTranslation();
  const { isNarrow, onOpenMobileMenu } =
    useOutletContext<ConfigOutletContext>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const access = useAppSelector((s) => s.auth.access);
  const names = useAppSelector((s) => s.auth.names);
  const username = useAppSelector((s) => s.auth.username);
  const displayName = names || username || "";

  // От коя секция е отворен модалът за създаване — тя определя типа на
  // устройството (модалът не предлага избор на тип при създаване).
  const [createVariant, setCreateVariant] = useState<
    null | "pos" | "fiscal" | "terminal"
  >(null);
  // Типът на последно отворения модал — остава и докато модалът се затваря,
  // иначе фискалният/терминалът изчезва рязко (POS има свой модал).
  const [lastCreateVariant, setLastCreateVariant] = useState<
    "pos" | "fiscal" | "terminal"
  >("pos");
  const openCreate = (variant: "pos" | "fiscal" | "terminal") => {
    setLastCreateVariant(variant);
    setCreateVariant(variant);
  };
  const { message } = App.useApp();
  const createDeviceMutation = useCreateDevice();

  const { data: devices = [] } = useDevices();
  const {
    data: users,
    isLoading: usersLoading,
    error: usersError,
  } = useUsers(undefined, false, "monitors");
  const { data: organization } = useOrganization();
  const orgUrl = organization?.name
    ? `https://${organization.name}.unrealsoft.cloud`
    : null;

  const isLoading = usersLoading;
  const hasInitialLoadError = usersError;

  // Създаване: GUID-ът и типът идват от модала; create endpoint-ът не
  // приема paymentMethods — конфигурират се чак в редакцията.
  const handleCreate = async (values: DeviceFormValues) => {
    try {
      await createDeviceMutation.mutateAsync({
        guid: values.guid,
        name: values.name,
        type: values.type,
        userIds: values.userIds,
        stationIds: values.stationIds,
        printLanguage: values.printLanguage,
        summarizeReceipt: values.summarizeReceipt,
        nonFiscalCopy: values.nonFiscalCopy,
        paymentMethods: values.paymentMethods,
      });
      message.success(t("devices.createSuccess"));
      setCreateVariant(null);
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      const fallback =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ||
        (error as { message?: string })?.message ||
        t("devices.createError");
      message.error(detail || fallback);
      throw error;
    }
  };

  const handleLogout = async () => {
    if (access) {
        try {
          await denyAccess();
        } catch (e) {
          console.error("Deny access error:", e);
        }
        dispatch(setAccess(false));
        navigate("/", { replace: true });
      } else {
        await dispatch(logout());
        navigate("/login", { replace: true });
      }
  };

  return (
    <div className="h-full flex flex-col" style={{ background: R.bg }}>
      <style>{`
        @keyframes rspin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      `}</style>

      {/* Top bar */}
      <div
        className="flex items-center justify-between flex-shrink-0"
        style={{
          padding: isNarrow ? "10px 16px" : "11px 28px",
          minHeight: isNarrow ? 48 : 60,
          borderBottom: `1px solid ${R.border}`,
          background: access
            ? `radial-gradient(ellipse 40% 120% at 100% 50%, ${R.brandTint} 0%, transparent 70%), ${R.surface}`
            : R.surface,
          gap: 10,
        }}
      >
        <div
          className="flex items-center"
          style={{ gap: 10, minWidth: 0, flex: 1 }}
        >
          {isNarrow && (
            <button
              onClick={onOpenMobileMenu}
              aria-label="Open menu"
              style={{
                height: 34,
                width: 34,
                borderRadius: 8,
                background: "transparent",
                border: "none",
                color: R.ink,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <MenuOutlined style={{ fontSize: 18 }} />
            </button>
          )}
          <h1
            style={{
              fontSize: isNarrow ? 18 : 22,
              fontWeight: 700,
              letterSpacing: -0.5,
              margin: 0,
              color: R.ink,
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {t("configSidebar.devices")}
          </h1>
        </div>

        <div
          className="flex items-center"
          style={{ gap: isNarrow ? 6 : 10, flexShrink: 0 }}
        >
          <ProfileDropdown
            displayName={displayName}
            username={username ?? ""}
            access={access}
            isNarrow={isNarrow}
            onLogout={handleLogout}
          />
        </div>
      </div>

      {/* Body */}
      <div
        className="flex-1 min-h-0 overflow-y-auto"
        style={{ padding: isNarrow ? "14px 14px 24px" : "22px 28px 32px" }}
      >
        {/* Разстоянието между секциите = горния отстъп на страницата */}
        <div className="flex flex-col" style={{ gap: isNarrow ? 14 : 22 }}>
          {isLoading ? (
            <div
              style={{
                padding: 48,
                display: "flex",
                justifyContent: "center",
                background: R.surface,
                border: `1px solid ${R.border}`,
                borderRadius: 14,
              }}
            >
              <Spin size="large" />
            </div>
          ) : hasInitialLoadError ? (
            <div
              style={{
                padding: "22px 24px",
                background: R.surface,
                border: `1px solid ${R.border}`,
                borderRadius: 14,
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 9,
                  background: "var(--color-danger-tint)",
                  color: R.danger,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <ExclamationCircleOutlined style={{ fontSize: 16 }} />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: R.ink,
                    letterSpacing: -0.2,
                  }}
                >
                  {t("devices.loadingError")}
                </div>
                <div
                  style={{
                    fontSize: 12.5,
                    color: R.textSoft,
                    marginTop: 2,
                  }}
                >
                  {t("devices.loadingErrorDesc")}
                </div>
              </div>
            </div>
          ) : users ? (
            <>
              {/* Три отделни секции — по една на тип устройство — всяка със
                  свой бутон за добавяне (той определя и типа в модала). */}
              <DevicesTable
                devices={devices.filter((device) => device.type === "pos")}
                users={users}
                variant="pos"
                onAddDevice={() => openCreate("pos")}
                isNarrow={isNarrow}
                orgUrl={orgUrl}
              />
              <DevicesTable
                devices={devices.filter((device) => device.type === "fiscal")}
                users={users}
                variant="fiscal"
                onAddDevice={() => openCreate("fiscal")}
                isNarrow={isNarrow}
                orgUrl={orgUrl}
              />
              <DevicesTable
                devices={devices.filter(
                  (device) => device.type === "paymentTerminal",
                )}
                users={users}
                variant="terminal"
                onAddDevice={() => openCreate("terminal")}
                isNarrow={isNarrow}
                orgUrl={orgUrl}
              />

              {/* Локален резерв (docs/edge.md): стига ли този браузър до
                  главната каса; включване/изключване на резерва. */}
              <EdgeStatusPanel isNarrow={isNarrow} />

              {/* Общият модал за създаване (същият ползва и редакцията). */}
              <DeviceFormModal
                open={createVariant !== null}
                initialType={
                  lastCreateVariant === "fiscal"
                    ? "fiscal"
                    : lastCreateVariant === "terminal"
                      ? "paymentTerminal"
                      : "pos"
                }
                users={users}
                devices={devices}
                onSubmit={handleCreate}
                onCancel={() => setCreateVariant(null)}
              />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default DevicesPage;
